/**
 * AUROS Tecnología
 * Ruta Shopify: assets/auros-tecnologia.js
 * JS progresivo: la landing conserva contenido, navegación y SEO sin JavaScript.
 */
(() => {
  const ROOT_SELECTOR = '[data-auros-tech]';
  const initialized = new WeakSet();

  const safePublish = (eventName, payload = {}) => {
    const eventPayload = {
      page_path: window.location.pathname,
      page_title: document.title,
      ...payload
    };

    try {
      if (window.Shopify?.analytics?.publish) {
        Promise.resolve(window.Shopify.analytics.publish(eventName, eventPayload)).catch(() => {});
      }
    } catch (_) {}

    window.dispatchEvent(new CustomEvent('auros:tech_event', {
      detail: {
        name: eventName,
        ...eventPayload
      }
    }));
  };

  const normalizeEventName = (trackName) => {
    const map = {
      hero_primary: 'auros:tech_hero_primary',
      hero_secondary: 'auros:tech_hero_secondary',
      line_view_all: 'auros:tech_line_view_all',
      featured_view_all: 'auros:tech_collection_select',
      product_select: 'auros:tech_product_select',
      closing_primary: 'auros:tech_closing_primary',
      closing_secondary: 'auros:tech_closing_secondary'
    };

    return map[trackName] || `auros:tech_${String(trackName || 'interaction').replace(/[^a-z0-9_:-]/gi, '_').toLowerCase()}`;
  };

  const bindTracking = (root) => {
    root.addEventListener('click', (event) => {
      const target = event.target.closest('[data-auros-tech-track]');
      if (!target || !root.contains(target)) return;

      const trackName = target.dataset.aurosTechTrack || 'interaction';
      const payload = {
        section_id: root.dataset.sectionId || '',
        interaction: trackName,
        label: target.dataset.aurosTechLabel || target.textContent?.trim().slice(0, 120) || '',
        destination: target.getAttribute('href') || '',
        product_id: target.dataset.aurosTechProductId || '',
        product_handle: target.dataset.aurosTechProductHandle || '',
        context: target.dataset.aurosTechContext || ''
      };

      safePublish(normalizeEventName(trackName), payload);
    });
  };

  const bindFaqTracking = (root) => {
    root.querySelectorAll('.auros-tech__faq-item').forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;

        const question = item.querySelector('summary span')?.textContent?.trim() || '';
        safePublish('auros:tech_faq_open', {
          section_id: root.dataset.sectionId || '',
          question
        });
      });
    });
  };

  const bindFaqAnimations = (root) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('animate' in Element.prototype)) return;

    root.querySelectorAll('.auros-tech__faq-item').forEach((item) => {
      const summary = item.querySelector('summary');
      const answer = item.querySelector('.auros-tech__faq-answer');
      if (!summary || !answer) return;

      let animation = null;
      let closing = false;
      let expanding = false;

      const onAnimationFinish = (open) => {
        item.open = open;
        item.style.height = '';
        item.style.overflow = '';
        answer.style.opacity = '';
        animation = null;
        closing = false;
        expanding = false;
      };

      const shrink = () => {
        closing = true;
        const startHeight = `${item.offsetHeight}px`;
        const endHeight = `${summary.offsetHeight}px`;

        if (animation) animation.cancel();
        item.style.overflow = 'hidden';

        animation = item.animate(
          { height: [startHeight, endHeight] },
          {
            duration: 170,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }
        );

        answer.animate(
          { opacity: [1, 0] },
          {
            duration: 120,
            easing: 'ease-out',
            fill: 'forwards'
          }
        );

        animation.addEventListener('finish', () => onAnimationFinish(false), { once: true });
      };

      const expand = () => {
        expanding = true;
        const startHeight = `${item.offsetHeight}px`;
        const endHeight = `${summary.offsetHeight + answer.scrollHeight}px`;

        if (animation) animation.cancel();
        item.style.overflow = 'hidden';

        animation = item.animate(
          { height: [startHeight, endHeight] },
          {
            duration: 210,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)'
          }
        );

        answer.animate(
          { opacity: [0, 1] },
          {
            duration: 160,
            delay: 20,
            easing: 'ease-out',
            fill: 'forwards'
          }
        );

        animation.addEventListener('finish', () => onAnimationFinish(true), { once: true });
      };

      const open = () => {
        item.style.height = `${item.offsetHeight}px`;
        item.open = true;
        window.requestAnimationFrame(expand);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (closing || !item.open) {
          open();
          return;
        }

        if (expanding || item.open) {
          shrink();
        }
      });
    });
  };

  const bindSectionViews = (root) => {
    if (!('IntersectionObserver' in window)) return;

    const observed = new WeakSet();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.45 || observed.has(entry.target)) return;

        observed.add(entry.target);
        observer.unobserve(entry.target);
        safePublish('auros:tech_section_view', {
          section_id: root.dataset.sectionId || '',
          content_section: entry.target.dataset.aurosTechObserve || '',
          context: entry.target.dataset.aurosTechLine || ''
        });
      });
    }, {
      threshold: [0.45]
    });

    root.querySelectorAll('[data-auros-tech-observe]').forEach((section) => observer.observe(section));
  };

  const bindSmoothHeroScroll = (root) => {
    const trigger = root.querySelector('[data-auros-tech-track="hero_primary"]');
    if (!trigger) return;

    trigger.addEventListener('click', (event) => {
      const href = trigger.getAttribute('href') || '';
      if (!href.startsWith('#') || href.length < 2) return;

      const target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  };

  const bindProductCarousels = (root) => {
    root.querySelectorAll('[data-auros-tech-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-auros-tech-carousel-track]');
      const previousButton = carousel.querySelector('.auros-tech__carousel-arrow--previous');
      const nextButton = carousel.querySelector('.auros-tech__carousel-arrow--next');
      if (!track || !previousButton || !nextButton) return;

      const getScrollStep = () => {
        const firstCard = track.querySelector('.auros-tech__product-slide');
        if (!firstCard) return track.clientWidth;

        const trackStyles = window.getComputedStyle(track);
        const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
        return firstCard.getBoundingClientRect().width + gap;
      };

      const updateArrowState = () => {
        const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        previousButton.disabled = track.scrollLeft <= 2;
        nextButton.disabled = track.scrollLeft >= maximumScroll - 2;
      };

      previousButton.addEventListener('click', () => {
        track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
      });

      nextButton.addEventListener('click', () => {
        track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
      });

      let isDragging = false;
      let hasDragged = false;
      let dragStartX = 0;
      let dragStartScrollLeft = 0;
      let activePointerId = null;
      let suppressNextClick = false;

      const finishDrag = (event) => {
        if (!isDragging) return;

        isDragging = false;
        suppressNextClick = hasDragged;
        track.classList.remove('is-dragging');

        if (activePointerId !== null && track.hasPointerCapture(activePointerId)) {
          track.releasePointerCapture(activePointerId);
        }

        activePointerId = null;
        if (event) updateArrowState();
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;

        isDragging = true;
        hasDragged = false;
        activePointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScrollLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', (event) => {
        if (!isDragging || event.pointerId !== activePointerId) return;

        const distance = event.clientX - dragStartX;
        if (Math.abs(distance) > 4) {
          hasDragged = true;
          suppressNextClick = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }

        if (!hasDragged) return;

        event.preventDefault();
        track.scrollLeft = dragStartScrollLeft - distance;
      });

      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('lostpointercapture', finishDrag);
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener('click', (event) => {
        if (!suppressNextClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressNextClick = false;
      }, true);
      track.addEventListener('scroll', updateArrowState, { passive: true });
      window.addEventListener('resize', updateArrowState, { passive: true });
      window.requestAnimationFrame(updateArrowState);
    });
  };

  const prepareHoverPerformance = (root) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('.auros-tech-card').forEach((element) => {
      element.addEventListener('pointerenter', () => element.classList.add('is-hover-prepared'), { once: true });
    });
  };

  const init = (root) => {
    if (!root || initialized.has(root)) return;

    initialized.add(root);
    root.classList.add('is-enhanced');
    bindTracking(root);
    bindFaqTracking(root);
    bindFaqAnimations(root);
    bindSectionViews(root);
    bindSmoothHeroScroll(root);
    bindProductCarousels(root);
    prepareHoverPerformance(root);

    safePublish('auros:tech_landing_view', {
      section_id: root.dataset.sectionId || ''
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
